import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/standard-button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { JiraProductDiscoveryLogo } from '@atlaskit/logo';
import { Box, xcss } from '@atlaskit/primitives';
import { N800 } from '@atlaskit/theme/colors';
import { token, useThemeObserver } from '@atlaskit/tokens';
// eslint-disable-next-line jira/import/no-parent-imports
import AvatarGroup from '../assets/avatar-group.svg';

export type InviteUserMessageProps = {
	onInviteUserClick: () => void;
	onDismissExpansionMessage: () => void;
};

export const InviteUserMessage = ({
	onInviteUserClick,
	onDismissExpansionMessage,
}: InviteUserMessageProps) => {
	const { colorMode } = useThemeObserver();
	const isDarkMode = colorMode === 'dark';
	return (
		<MessageWrapper>
			<MessageHeader isDarkMode={isDarkMode}>
				<IconWrapper>
					<Button
						onClick={onDismissExpansionMessage}
						appearance="subtle"
						iconBefore={<CrossIcon label="dismiss" size="medium" />}
					/>
				</IconWrapper>
				<img alt="Jira Product Discovery" src={AvatarGroup} />
			</MessageHeader>
			<Box xcss={messageContentStyles}>
				<JiraProductDiscoveryLogo appearance="brand" size="small" />
				<MessageTitle>Add your team to help collect and prioritize ideas </MessageTitle>
				<MessageDescription>
					Collate, prioritize and deliver ideas all on one platform, together.
				</MessageDescription>
				<Box xcss={messageFooterStyles}>
					<Button onClick={onInviteUserClick}>Add team members</Button>
				</Box>
			</Box>
		</MessageWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageWrapper = styled.div({
	width: '360px',
	height: '300px',
	backgroundColor: token('color.background.input', '#FFFFFF'),
	borderRadius: '3px',
	marginBottom: token('space.200', '16px'),
	position: 'relative',
	boxShadow: token(
		'elevation.shadow.overflow',
		'0px 0px 8px 0px rgba(9, 30, 66, 0.16), 0px 0px 1px 0px rgba(9, 30, 66, 0.12)',
	),
});

const messageFooterStyles = xcss({
	marginTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageTitle = styled.div({
	color: token('color.text', '#172B4D'),
	width: '328px',
	fontSize: '20px',
	fontStyle: 'normal',
	fontWeight: 500,
	lineHeight: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageDescription = styled.div({
	color: token('color.text.subtle', N800),
	marginTop: token('space.100', '8px'),
	fontSize: '14px',
	fontStyle: 'normal',
	fontWeight: 400,
	lineHeight: '20px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageHeader = styled.div<{ isDarkMode: boolean }>({
	height: '98px',
	borderRadius: '3px 3px 0 0',
	justifyContent: 'center',
	alignItems: 'center',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: ({ isDarkMode }) => (isDarkMode ? '#09326C' : '#CCE0FF'),
});

const messageContentStyles = xcss({
	padding: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	position: 'absolute',
	top: token('space.150', '12px'),
	right: token('space.150', '12px'),
});
